import React, { useState, useEffect } from 'react';
import { ModalBody, PopoverBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { loadAllTags, changeSortConfigTag, loadAllTagsTranslate } from 'redux/actions/tagActions';

import { TextField } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { ButtonLabelIcon, Button } from 'shared/components/Buttons';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as TranslateIcon } from 'assets/icon-translate.svg';
import { LabelIcon } from 'shared/components/Labels';
import TagsTable from './TagsTable';
import TagForm from './TagForm';
import TranslateFormModal from './TranslateFormModal';
import { TagFormModal, Popover, OptionMenu, TagsWrapper } from './styles';

const Tags = () => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const tagState = useSelector((state) => state.tagState);
  const { sortConfig } = tagState;
  const [openFormModal, setOpenFormModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentTag, setCurrentTag] = useState(null);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [{ loading }, getTagsData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );
  const [, getTagsTranslateData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );
  const getSortIcon = (fieldName) => {
    const sortOption = sortConfig.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return sortOption.asc ? (
        <ArrowUpIcon fill={colors.black} />
      ) : (
        <ArrowDownIcon fill={colors.black} />
      );
    }
    return null;
  };

  const onSort = (fieldName) => {
    dispatch(changeSortConfigTag(fieldName));
  };

  const refreshTagsData = async () => {
    try {
      const searchParam =
        sortConfig.length > 0
          ? {
              filters: [
                ...sortConfig,
                {
                  model: 'I18List',
                  field: 'I18Code',
                  filter: [selectedDefaultLanguage],
                },
              ],
              reduce: {
                limit: 0,
                skip: 0,
              },
            }
          : {
              filters: [
                {
                  model: 'Tag',
                  field: 'Name',
                  asc: false,
                },
                {
                  model: 'I18List',
                  field: 'I18Code',
                  filter: [selectedDefaultLanguage],
                },
              ],
              reduce: {
                limit: 0,
                skip: 0,
              },
            };
      const {
        data: { data },
      } = await getTagsData({
        data: searchParam,
      });
      dispatch(loadAllTags(data.filter((x) => x.id)));
    } catch (error) {
      dispatch(loadAllTags([]));
    }
  };

  const refreshTagsTranslateData = async () => {
    try {
      const searchParam = {
        filters: [
          {
            model: 'Tag',
            field: 'Name',
            asc: false,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      };
      const {
        data: { data },
      } = await getTagsTranslateData({
        data: searchParam,
      });
      dispatch(loadAllTagsTranslate(data.filter((x) => x.id)));
    } catch (error) {
      dispatch(loadAllTagsTranslate([]));
    }
  };

  useEffect(() => {
    refreshTagsData();
  }, [sortConfig, selectedDefaultLanguage]);

  useEffect(() => {
    refreshTagsTranslateData();
  }, []);

  const searchTags = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = await getTagsData({
          data: {
            filters: [
              {
                model: 'I18List',
                field: 'Name',
                filter: [value],
                isRegex: true,
              },
              {
                model: 'I18List',
                field: 'I18Code',
                filter: [selectedDefaultLanguage],
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        dispatch(loadAllTags(data.filter((x) => x.id)));
      } catch (error) {
        dispatch(loadAllTags([]));
      }
    } else {
      setSearchValue('');
      try {
        const {
          data: { data },
        } = await getTagsData({
          data: {
            filters: [
              {
                model: 'I18List',
                field: 'Name',
                filter: [''],
                isRegex: true,
              },
              {
                model: 'I18List',
                field: 'I18Code',
                filter: [selectedDefaultLanguage],
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        dispatch(loadAllTags(data));
      } catch (error) {
        dispatch(loadAllTags([]));
      }
    }
  };

  const handleRefresh = () => {
    refreshTagsData();
    refreshTagsTranslateData();
  };

  const handleToggleOption = () => {
    setOpenFormModal(true);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
    setCurrentTag(null);
  };

  const onTranslate = () => {
    setShowTranslateModal(!showTranslateModal);
  };

  return (
    <TagsWrapper className="my-3">
      <div className="my-3">
        <Button
          className="translate-button"
          type="button"
          color="secondary"
          label={t('general_translate')}
          icon={<TranslateIcon />}
          onClick={onTranslate}
        />
      </div>

      <div className="d-flex justify-content-end action-header-mobile">
        <div className="form w-25 h-50 mr-4 w-container-search-mobile">
          <TextField
            Icon={() => null}
            borderRadius={2}
            className="mb-4 w-search m-0 h-50"
            inputClassName="w-100"
            buttonClassName=""
            withButtonClear
            rightIcon={<SearchIcon fill={colors.black} />}
            onChange={searchTags}
            value={searchValue ?? ''}
          />
        </div>
        <div className="d-flex btn-header-action">
          <ButtonLabelIcon
            type="button"
            Icon={() => <SortByIcon fill={colors.black} />}
            className="button-icon mr-1"
            text={t('general_order_by')}
            id="PopoverFocus"
          />
          <ButtonLabelIcon
            id="refresh-icon"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={handleRefresh}
          />
          <ButtonLabelIcon
            id="add-icon"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon"
            onClick={handleToggleOption}
          />
        </div>
      </div>

      <TagsTable
        loading={loading}
        setCurrentTag={setCurrentTag}
        setOpenFormModal={setOpenFormModal}
        refreshTagsData={refreshTagsData}
      />
      <Popover trigger="legacy" placement="bottom" target="PopoverFocus">
        <PopoverBody>
          <OptionMenu onClick={() => onSort('Name')} aria-hidden="true">
            <LabelIcon
              label={t('clients_name_column')}
              color=""
              className="ml-1 body-text--primary"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Name')}
            />
          </OptionMenu>
        </PopoverBody>
      </Popover>
      {showTranslateModal && (
        <TranslateFormModal
          onCloseModal={onTranslate}
          refreshTagsTranslateData={refreshTagsTranslateData}
        />
      )}
      <TagFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <ModalBody>
          <TagForm
            onCancel={onCloseFormModal}
            currentTag={currentTag}
            refreshTagsData={refreshTagsData}
          />
        </ModalBody>
      </TagFormModal>
    </TagsWrapper>
  );
};

export default Tags;
